/**
 * DeleteProductConfirmationModal - Tipo 1: Modal de Confirmación
 *
 * Usa el componente global ConfirmationModal para confirmar eliminación de productos.
 * Después de la eliminación, muestra un Snackbar con el resultado.
 */

import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, Typography, Box } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Product } from '../../../types/product.types';
import { ConfirmationModal } from '../../ui/Modal';
import { colors } from '../../../styles/theme/colors';
import { CATALOG_CTA_GRADIENT, CATALOG_CTA_GRADIENT_HOVER } from '../../products/catalogGradients';

export interface DeleteResult {
  productId: number;
  success: boolean;
  error?: {
    reason: string;
    errorCode: number;
    countAssociates?: Array<number>;
    associationTypes?: Array<string>;
  };
}

export interface DeleteProductConfirmationModalProps {
  open: boolean;
  products: Product[];
  onClose: () => void;
  onConfirm: () => Promise<DeleteResult[]>;
  onDeleteComplete?: (results: DeleteResult[]) => void;
  loading?: boolean;
}

const DeleteProductConfirmationModal: React.FC<DeleteProductConfirmationModalProps> = ({
  open,
  products,
  onClose,
  onConfirm,
  onDeleteComplete,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  loading = false,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      const results = await onConfirm();
      // Cerrar el modal después de la eliminación
      onClose();
      // Notificar al componente padre con los resultados
      if (onDeleteComplete) {
        onDeleteComplete(results);
      }
    } catch (error) {
      console.error('Error deleting products:', error);
      // En caso de error, crear resultados de error para todos los productos
      const errorResults: DeleteResult[] = products.map((p) => ({
        productId: p.id,
        success: false,
        error: {
          reason: 'Error inesperado al eliminar el producto',
          errorCode: 0,
        },
      }));
      onClose();
      if (onDeleteComplete) {
        onDeleteComplete(errorResults);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // Validar que haya productos antes de acceder a sus propiedades
  if (!products || products.length === 0) {
    return null;
  }

  // Fase de confirmación: usar ConfirmationModal
  // Formato diferente según cantidad de productos
  const customContent =
    products.length === 1 ? (
      // Formato de caja para un solo producto (igual a UnreserveConfirmationModal)
      <Box
        sx={(theme) => ({
          p: 2.5,
          borderRadius: '8px',
          backgroundColor:
            theme.palette.mode === 'dark' ? theme.palette.background.paper : colors.grey[50],
          border: `1px solid ${theme.palette.divider}`,
          textAlign: 'left',
          mt: 2,
        })}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontSize: '0.75rem',
            fontWeight: 500,
            display: 'block',
            mb: 1,
          }}
        >
          Producto
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 1.5,
          }}
        >
          {products[0].name}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontSize: '0.8125rem',
          }}
        >
          Código: <strong>{products[0].code}</strong>
        </Typography>
      </Box>
    ) : (
      // Formato de tabla para múltiples productos
      <Box
        sx={(theme) => ({
          mt: 2,
          borderRadius: '8px',
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        })}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={(theme) => ({
                backgroundColor: theme.palette.background.paper,
              })}
            >
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.secondary' }}>
                Código
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.secondary' }}>
                Nombre
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id}>
                <TableCell sx={{ fontSize: '0.875rem', color: 'text.primary' }}>
                  {product.code}
                </TableCell>
                <TableCell sx={{ fontSize: '0.875rem', color: 'text.primary' }}>
                  {product.name}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );

  const title =
    products.length === 1
      ? `Eliminar Producto ${products[0]?.name || ''}`
      : `Eliminar ${products.length} Productos`;
  const checkboxLabel =
    products.length === 1
      ? '¿Desea eliminar este producto?'
      : `¿Desea eliminar estos ${products.length} productos?`;

  return (
    <ConfirmationModal
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title={title}
      message={
        products.length === 1
          ? `¿Está seguro que desea eliminar el producto "${products[0]?.name || ''}"?`
          : `¿Está seguro que desea eliminar estos ${products.length} productos?`
      }
      confirmText={isDeleting ? 'Eliminando...' : 'Eliminar'}
      cancelText="Cancelar"
      variant="default"
      confirmButtonGradient={CATALOG_CTA_GRADIENT}
      confirmButtonGradientHover={CATALOG_CTA_GRADIENT_HOVER}
      accentIconColor={colors.primary.main}
      icon={<DeleteIcon />}
      loading={isDeleting}
      requireCheckbox={true}
      checkboxLabel={checkboxLabel}
      customContent={customContent}
    />
  );
};

export default DeleteProductConfirmationModal;
